/* Price distribution above the collection price filter (ported from the whole-house page), and the price slider's stops.
   Bars show how many product options fall in each price band; bars inside the chosen range are navy.
   Lane H (AJ 2026-10-10 21:54, "price slider snaps to real product prices"): the same read also gives each product's
   lowest price (the "From" on its card), which the price slider uses as its stops (facets.js, PriceRange). */
(() => {
  if (window.cqHistogramInit) return;
  window.cqHistogramInit = true;
  const BINS = 32;
  const cache = new Map();
  const skipTypes = new Set(['OPTIONS_HIDDEN_PRODUCT', 'mws_apo_generated', 'Gift Card']);

  const collectionPath = () => {
    const m = location.pathname.match(/^\/collections\/[^/]+/);
    return m ? m[0] : null;
  };

  const loadPrices = async (path) => {
    if (cache.has(path)) return cache.get(path);
    const key = 'cqHist2:' + path;
    try {
      const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (saved && Array.isArray(saved.prices) && Array.isArray(saved.lows)) { cache.set(path, saved); return saved; }
    } catch (e) { /* storage unavailable */ }
    const prices = [];
    const lows = [];
    for (let page = 1; page <= 8; page++) {
      const res = await fetch(`${path}/products.json?limit=250&page=${page}`, { credentials: 'same-origin' });
      if (!res.ok) break;
      const data = await res.json();
      const list = data.products || [];
      list.forEach((p) => {
        if (skipTypes.has(p.product_type)) return;
        let low = Infinity;
        (p.variants || []).forEach((v) => { const n = parseFloat(v.price); if (Number.isFinite(n)) { prices.push(n); low = Math.min(low, n); } });
        if (Number.isFinite(low)) lows.push(low);
      });
      if (list.length < 250) break;
    }
    const data = { prices, lows };
    cache.set(path, data);
    try { sessionStorage.setItem(key, JSON.stringify(data)); } catch (e) { /* ignore */ }
    return data;
  };

  // Lane X (AJ 2026-10-11 05:48, "why is price slider not working in search?"): the search page has no products.json feed, so
  // its stops are the lowest price of each product the search finds, which sections/predictive-search.liquid lists when it
  // is rendered on /search (250 products per request, up to four requests). facets.js then moves the handles stop by stop.
  // Lane X2 (2026-10-11): those requests wait for the shopper's first touch of the slider (pointerdown, focus or a key on a
  // handle or a price box), so a search page loads with no extra /search requests; until then the slider is continuous.
  const searchLoads = new Map();
  const searchKey = (q) => 'cqSearchLows1:' + q.toLowerCase();

  // Stops already read in this tab (memory or sessionStorage): no request, so they apply as soon as the slider renders.
  const savedSearchLows = (q) => {
    const key = searchKey(q);
    if (cache.has(key)) return cache.get(key);
    try {
      const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (Array.isArray(saved)) { cache.set(key, saved); return saved; }
    } catch (e) { /* storage unavailable */ }
    return null;
  };

  const fetchSearchLows = async (q) => {
    const lows = [];
    for (let page = 1; page <= 4; page++) {
      const params = new URLSearchParams({ q, type: 'product', 'options[prefix]': 'last', page: String(page), section_id: 'predictive-search' });
      const res = await fetch(`${location.pathname}?${params}`, { credentials: 'same-origin' });
      if (!res.ok) break;
      const tpl = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('template[data-cq-search-prices]');
      if (!tpl) break;
      JSON.parse(tpl.innerHTML.trim() || '[]').forEach((cents) => lows.push(cents / 100));
      if (page >= Number(tpl.dataset.pages || 1)) break;
    }
    cache.set(searchKey(q), lows);
    try { sessionStorage.setItem(searchKey(q), JSON.stringify(lows)); } catch (e) { /* ignore */ }
    return lows;
  };

  // One set of requests per query, shared by every slider on the page (the filter form re-renders after each change).
  const loadSearchLows = (q) => {
    if (!searchLoads.has(q)) searchLoads.set(q, fetchSearchLows(q).catch((e) => { searchLoads.delete(q); throw e; }));
    return searchLoads.get(q);
  };

  // Switching the track from dollars to stops mid-drag would move the handle under the finger, so stops that arrive
  // during a drag wait for the release (and for the slider's own change handler, which runs in the same task).
  const dragging = new Set();
  const release = () => {
    if (!dragging.size) return;
    const ranges = [...dragging];
    dragging.clear();
    setTimeout(() => ranges.forEach((range) => {
      const apply = range.cqApplyStops;
      range.cqApplyStops = null;
      if (apply) apply();
    }), 0);
  };
  const applySearch = (range, lows) => {
    const apply = () => {
      range.cqStops = lows;
      range.dispatchEvent(new CustomEvent('cq:price-stops'));
    };
    if (dragging.has(range)) range.cqApplyStops = apply;
    else apply();
  };

  const searchQuery = () => {
    const field = document.querySelector('input[name="q"][form="FacetsFilterForm"]');
    return ((field && field.value) || new URLSearchParams(location.search).get('q') || '').trim();
  };

  const initSearch = (range) => {
    range.dataset.cqReady = '1';
    const q = searchQuery();
    if (!q) return;
    range.addEventListener('pointerdown', (event) => { if (event.target.type === 'range') dragging.add(range); }, true);
    const saved = savedSearchLows(q);
    if (saved) { applySearch(range, saved); return; }
    const pending = searchLoads.get(q);
    if (pending) { pending.then((lows) => applySearch(range, lows), () => {}); return; }
    const events = ['pointerdown', 'focusin', 'keydown'];
    const onTouch = (event) => {
      if (!(event.target instanceof HTMLInputElement) || range.cqStopsAsked) return;
      range.cqStopsAsked = true;
      loadSearchLows(q).then((lows) => applySearch(range, lows), () => { range.cqStopsAsked = false; /* the slider stays continuous */ });
    };
    events.forEach((type) => range.addEventListener(type, onTouch, true));
  };

  const bounds = (box) => {
    const range = box.closest('price-range') || box.parentElement;
    const inputs = range ? range.querySelectorAll('input[type="number"], input[inputmode="decimal"]') : [];
    const max = parseFloat(box.dataset.max) || 0;
    const read = (el, fallback) => { const n = parseFloat(el && el.value); return Number.isFinite(n) ? n : fallback; };
    const lo = read(inputs[0], 0);
    const hi = read(inputs[1], max);
    return { lo: Math.max(0, Math.min(lo, hi)), hi: Math.min(max, Math.max(lo, hi)), max };
  };

  const paint = (box, prices) => {
    const { lo, hi, max } = bounds(box);
    if (!max || !prices.length) { box.hidden = true; box.dataset.cqEmpty = ''; return; }
    const bins = Array.from({ length: BINS }, () => []);
    prices.forEach((v) => bins[Math.min(BINS - 1, Math.floor((v / max) * BINS))].push(v));
    const peak = Math.max(1, ...bins.map((b) => b.length));
    box.hidden = false;
    box.innerHTML = bins.map((b) => {
      const sel = b.some((v) => v >= lo && v <= hi);
      const h = b.length ? Math.max(6, (b.length / peak) * 100) : 0;
      return `<span class="${sel ? 'is-selected' : ''}" style="--cq-bar:${h}%"></span>`;
    }).join('');
  };

  const init = async (box) => {
    if (box.dataset.cqReady) return;
    box.dataset.cqReady = '1';
    const path = collectionPath();
    if (!path) { box.hidden = true; box.dataset.cqEmpty = ''; return; }
    box.hidden = true;
    try {
      const { prices, lows } = await loadPrices(path);
      paint(box, prices);
      const range = box.closest('price-range') || box.parentElement;
      // The slider's stops: kept on the element in case facets.js has not defined price-range yet.
      range.cqStops = lows;
      range.dispatchEvent(new CustomEvent('cq:price-stops'));
      range.addEventListener('input', () => paint(box, prices));
      range.addEventListener('change', () => paint(box, prices));
    } catch (e) { box.hidden = true; box.dataset.cqEmpty = ''; }
  };

  const scan = () => {
    document.querySelectorAll('[data-cq-histogram]:not([data-cq-ready])').forEach(init);
    document.querySelectorAll('price-range[data-cq-search-stops]:not([data-cq-ready])').forEach(initSearch);
  };
  const start = () => {
    document.addEventListener('pointerup', release, true);
    document.addEventListener('pointercancel', release, true);
    scan();
    new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  };
  if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 1500 });
  else setTimeout(start, 300);
})();
